/**
 * MiniProjectTeamTab — Mini-Project Settings modal Team tab.
 *
 * POLICY_TEAM_ROSTERS.md §3. The Mini-Project team table IS the ACL.
 * One Lead, zero-to-nine Members. 10-person hard cap (spec §8.3).
 *
 * Pattern ported from src/Components/CommunitySettingsModal/CommunityEnablersTab.tsx
 * with these Mini-Project-specific differences:
 *   - L4E users only (no external members tab in the add flow).
 *   - Role enum ('lead' | 'member') instead of pure-display roles.
 *   - Lead row cannot be hidden — the Lead is the public accountability
 *     contact for the project.
 *   - 10-member hard cap with a visible "N / 10" counter; add button
 *     disabled when at cap.
 *   - Lead promotion goes through a dedicated "Transfer Lead" affordance
 *     on Member rows. Atomic on the server.
 *
 * Edits live in the in-memory store (src/Data/MiniProjectConfig/miniProjectTeam.ts)
 * and persist via the parent modal's Save button, which calls
 * persistMiniProjectTeamToApi. Cancel discards via discardLocalEdits.
 *
 * Event model mirrors Geo Community: l4e_miniproject_team_changed for
 * Settings-tab live re-render; l4e_miniproject_team_persisted (fired by
 * the store after a successful save) is what public surfaces should
 * listen to in chunk 3c.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { Tray } from "../Tray/Tray";
import FlipTeamMemberCard from "../TeamMemberCard/FlipTeamMemberCard";
import MiniProjectRolesSection from "./MiniProjectRolesSection";
import { useApiSecurityData } from "../../api/useApiSecurityData";
import { mediaSrc } from "../../api/mediaUrl";
import {
  getTeamForMiniProject,
  setTeamForMiniProject,
  upsertTeamMember,
  removeTeamMember,
  hydrateMiniProjectTeamFromApi,
  type LocalMiniProjectTeamMember,
} from "../../Data/MiniProjectConfig/miniProjectTeam";
import { getRolesForMiniProject } from "../../Data/MiniProjectConfig/miniProjectRoles";

/** Minimal user shape consumed by the picker + row display.
 *  Mirrors the loose shape the existing typeahead surfaces accept
 *  (RoleAssignmentModal, ProjectOwnersAdminsPanel). */
interface L4eUser {
  userGuid: string;
  displayName?: string | null;
  name?: string | null;
  email?: string | null;
  screenName?: string | null;
  profileImageUrl?: string | null;
}

const TEAM_CAP = 10; // spec §8.3

/* ── GUID generator (uppercase, RFC4122 v4-style) ─────────────────── */
function generateGuid(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  }).toUpperCase();
}

interface Props {
  miniProjectGuid: string;
  currentUserGuid: string;
  /** Bumped when in-memory team state diverges from the server. The
   *  parent modal folds this into its Save-button-enabled state and
   *  cleans up via discardLocalEdits on Cancel. */
  onDirty?: () => void;
}

/* ──────────────────────────────────────────────────────────────────
 *  Inline style tokens (CSS tokens via var())
 * ────────────────────────────────────────────────────────────────── */
const styles = {
  container: {
    padding: 20,
    display: "flex",
    flexDirection: "column" as const,
    gap: 16,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: "var(--text-primary)",
  },
  subtitle: {
    fontSize: 12,
    color: "var(--text-tertiary)",
    marginBlockStart: 4,
  },
  counter: {
    fontSize: 12,
    color: "var(--text-tertiary)",
    fontWeight: 500,
    marginInlineStart: 12,
  },
  counterAtCap: {
    color: "var(--color-warning)",
    fontWeight: 600,
  },
  list: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 6,
  },
  listRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    background: "var(--bg-surface)",
    border: "1px solid var(--border-default)",
    borderRadius: 8,
    cursor: "grab",
    transition: "background 0.1s, border-color 0.1s",
  },
  listRowLead: {
    borderColor: "var(--info-text)",
    boxShadow: "0 0 0 1px rgba(56, 189, 248, 0.15)",
  },
  listRowDragging: {
    opacity: 0.5,
  },
  listRowDragOver: {
    borderColor: "var(--accent-secondary)",
    borderStyle: "dashed" as const,
  },
  dragHandle: {
    color: "var(--text-tertiary)",
    fontSize: 14,
    cursor: "grab",
    userSelect: "none" as const,
    width: 18,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "var(--bg-elevated)",
    border: "1px solid var(--border-default)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--text-secondary)",
    fontSize: 13,
    fontWeight: 600,
    flexShrink: 0,
    overflow: "hidden",
  },
  rowText: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    minWidth: 0,
  },
  rowTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text-primary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  rowName: {
    fontSize: 12,
    color: "var(--text-tertiary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  badge: {
    display: "inline-block",
    marginInlineStart: 8,
    padding: "1px 8px",
    fontSize: 11,
    fontWeight: 600,
    color: "var(--bg-elevated)",
    background: "var(--info-text)",
    borderRadius: 10,
    verticalAlign: "middle" as const,
  },
  hiddenBadge: {
    display: "inline-block",
    marginInlineStart: 8,
    padding: "1px 8px",
    fontSize: 11,
    fontWeight: 600,
    color: "var(--text-secondary)",
    background: "var(--bg-hover)",
    border: "1px dashed var(--border-default)",
    borderRadius: 10,
    verticalAlign: "middle" as const,
  },
  rowActions: {
    display: "flex",
    gap: 6,
  },
  smallButton: {
    background: "var(--bg-hover)",
    border: "1px solid var(--border-default)",
    borderRadius: 6,
    color: "var(--text-secondary)",
    fontSize: 12,
    fontWeight: 600,
    padding: "5px 10px",
    cursor: "pointer",
  },
  dangerButton: {
    color: "var(--color-error)",
  },
  primaryButton: {
    background: "var(--accent-bg, var(--bg-emphasis, var(--text-primary)))",
    border: "1px solid var(--accent-border, var(--text-primary))",
    borderRadius: 6,
    color: "var(--bg-elevated)",
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 16px",
    cursor: "pointer",
  },
  addButton: {
    background: "var(--accent-secondary)",
    border: "none",
    borderRadius: 6,
    color: "var(--text-primary)",
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 14px",
    cursor: "pointer",
  },
  addButtonDisabled: {
    background: "var(--bg-hover)",
    color: "var(--text-muted)",
    cursor: "not-allowed",
  },
  emptyState: {
    padding: 24,
    textAlign: "center" as const,
    color: "var(--text-tertiary)",
    fontSize: 13,
    background: "var(--bg-surface)",
    border: "1px dashed var(--border-default)",
    borderRadius: 8,
  },
  trayBody: {
    padding: 20,
    display: "flex",
    flexDirection: "column" as const,
    gap: 16,
  },
  trayFooter: {
    padding: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  cardPreviewWrap: {
    display: "flex",
    justifyContent: "center",
    paddingBlock: 8,
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "var(--text-tertiary)",
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
    marginBlockEnd: 4,
  },
  inlineInput: {
    background: "var(--bg-elevated)",
    border: "1px solid var(--border-default)",
    borderRadius: 6,
    padding: "6px 10px",
    color: "var(--text-primary)",
    fontSize: 13,
    width: "100%" as const,
  },
  toggleRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "10px 12px",
    background: "var(--bg-elevated)",
    border: "1px solid var(--border-default)",
    borderRadius: 6,
  },
  toggleRowDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  helpText: {
    fontSize: 12,
    color: "var(--text-tertiary)",
    marginBlockStart: 4,
    lineHeight: 1.4,
  },
  capWarning: {
    fontSize: 12,
    color: "var(--color-warning)",
    marginBlockStart: 4,
  },
  confirmBackdrop: {
    position: "fixed" as const,
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
  },
  confirmModal: {
    background: "var(--bg-elevated)",
    border: "1px solid var(--border-default)",
    borderRadius: 12,
    padding: 20,
    minWidth: 360,
    maxWidth: "90vw",
    display: "flex",
    flexDirection: "column" as const,
    gap: 12,
  },
  confirmTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "var(--text-primary)",
    margin: 0,
  },
  confirmBody: {
    fontSize: 13,
    color: "var(--text-secondary)",
    lineHeight: 1.5,
  },
  userResults: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 4,
    maxBlockSize: 320,
    overflowY: "auto" as const,
    border: "1px solid var(--border-default)",
    borderRadius: 6,
    background: "var(--bg-surface)",
  },
  userResultEmpty: {
    padding: 16,
    textAlign: "center" as const,
    fontSize: 12,
    color: "var(--text-tertiary)",
  },
  userResultItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    background: "transparent",
    border: "none",
    borderBlockEnd: "1px solid var(--border-subtle, var(--border-default))",
    cursor: "pointer",
    textAlign: "start" as const,
    width: "100%" as const,
  },
  userResultAvatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "var(--bg-elevated)",
    border: "1px solid var(--border-default)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--text-secondary)",
    fontSize: 12,
    fontWeight: 600,
    flexShrink: 0,
    overflow: "hidden",
  },
  userResultText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as const,
  },
  userResultName: {
    fontSize: 13,
    color: "var(--text-primary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  userResultEmail: {
    fontSize: 11,
    color: "var(--text-tertiary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
} satisfies Record<string, React.CSSProperties>;

/* ──────────────────────────────────────────────────────────────────
 *  Helpers
 * ────────────────────────────────────────────────────────────────── */

function effectiveTitle(m: LocalMiniProjectTeamMember): string {
  const t = (m.displayTitle || "").trim();
  if (t) return t;
  return m.role === "lead" ? "Lead" : "Member";
}

function sortRoster(members: LocalMiniProjectTeamMember[]): LocalMiniProjectTeamMember[] {
  return [...members].sort((a, b) => {
    if (a.role === "lead" && b.role !== "lead") return -1;
    if (b.role === "lead" && a.role !== "lead") return 1;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.joinedAt.localeCompare(b.joinedAt);
  });
}

function smallAvatar(user: L4eUser | undefined): React.ReactNode {
  // Use the L4E profile photo when we have it; fall back to a name
  // initial; final fallback is a generic "?" so an unresolved row is
  // still distinguishable from a resolved one.
  if (user?.profileImageUrl) {
    return (
      <img
        src={mediaSrc(user.profileImageUrl) ?? undefined}
        alt=""
        style={{ ...styles.avatar, objectFit: "cover" as const }}
      />
    );
  }
  const name = user?.displayName || user?.name || user?.screenName || "?";
  const initial = name.trim().charAt(0).toUpperCase() || "?";
  return <div style={styles.avatar}>{initial}</div>;
}

/** Resolve a user record to the best human-readable label. Falls back
 *  to a short GUID prefix when securityUsers hasn't loaded yet or the
 *  account no longer exists (deactivated user with a still-active team
 *  membership row). */
function resolveUserDisplayName(user: L4eUser | undefined, userGuid: string): string {
  if (user?.displayName) return user.displayName;
  if (user?.name) return user.name;
  if (user?.screenName) return user.screenName;
  if (user?.email) return user.email;
  return `${userGuid.slice(0, 8)}…`;
}

/* ──────────────────────────────────────────────────────────────────
 *  Main component
 * ────────────────────────────────────────────────────────────────── */

const MiniProjectTeamTab: React.FC<Props> = ({ miniProjectGuid, currentUserGuid, onDirty }) => {
  const { t } = useAppTranslation("projectConfig");

  // Force re-render when the in-memory store mutates. Settings-tab
  // consumer pattern — see communityEnablers.ts comments.
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    window.addEventListener("l4e_miniproject_team_changed", bump);
    return () => window.removeEventListener("l4e_miniproject_team_changed", bump);
  }, []);

  // Hydrate on first mount. Safe to call repeatedly — the store dedupes
  // by mini_project_guid.
  useEffect(() => {
    void hydrateMiniProjectTeamFromApi(miniProjectGuid);
  }, [miniProjectGuid]);

  // DEV-1088 follow-up. Re-hydrate from the API when a remote change
  // lands (e.g. an invitee just accepted a role invitation and the
  // backend auto-added them to the team). The SignalR handler in
  // src/api/signalrConnection.ts dispatches l4e_miniproject_team_persisted
  // on every SecurityUpdated push; the TeamSection on About already
  // listens to this. The Settings tab needs the same wiring so its
  // local in-memory store doesn't go stale while the Lead is editing.
  // The store dedupes on mini_project_guid; refetch is cheap.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { miniProjectGuid?: string } | undefined;
      if (!detail?.miniProjectGuid) return;
      if (detail.miniProjectGuid.toUpperCase() !== miniProjectGuid.toUpperCase()) return;
      void hydrateMiniProjectTeamFromApi(miniProjectGuid);
    };
    window.addEventListener("l4e_miniproject_team_persisted", handler);
    return () => window.removeEventListener("l4e_miniproject_team_persisted", handler);
  }, [miniProjectGuid]);

  // POLICY_TEAM_ROSTERS.md §3.4 — adding to the team is a direct insert.
  // The added user gets a bell notification but does not need to accept,
  // so the picker scope is "any active L4E user" rather than the more
  // restrictive accepted-contacts list that Geo Community Leaders uses.
  // Same client-side filter pattern proven in RoleAssignmentModal,
  // ProjectOwnersAdminsPanel, CommunityPermissionsV2Panel. DEV-932:
  // securityUsers is async-loaded; deps tracked below.
  const { securityUsers } = useApiSecurityData();
  const allUsers = useMemo<L4eUser[]>(() => {
    const rows = (securityUsers || []) as any[];
    return rows
      .map((u) => ({
        userGuid: String(u.user_guid || u.userGuid || ""),
        displayName: u.display_name ?? u.displayName ?? null,
        name: u.name ?? null,
        email: u.email ?? null,
        screenName: u.screen_name ?? u.screenName ?? null,
        profileImageUrl: u.profile_image_url ?? u.profileImageUrl ?? null,
      }))
      .filter((u) => !!u.userGuid);
  }, [securityUsers]);

  // userGuid (upper) → user record, used to resolve display names in
  // the roster rows so we render "Alex Doe" instead of a GUID prefix.
  const userByGuid = useMemo(() => {
    const m = new Map<string, L4eUser>();
    for (const u of allUsers) m.set(u.userGuid.toUpperCase(), u);
    return m;
  }, [allUsers]);

  const members = useMemo(() => {
    // Touch version so the memo recomputes on store mutations.
    void version;
    return sortRoster(getTeamForMiniProject(miniProjectGuid));
  }, [miniProjectGuid, version]);

  const activeCount = members.length;
  const atCap = activeCount >= TEAM_CAP;

  const markDirty = useCallback(() => {
    onDirty?.();
  }, [onDirty]);

  /* ── Mutation helpers ── */

  const handleRemove = useCallback(
    (member: LocalMiniProjectTeamMember) => {
      if (member.role === "lead") {
        window.alert(t("miniProjectTeam.leadCannotBeRemoved"));
        return;
      }
      const isSelf = member.userGuid.toUpperCase() === currentUserGuid.toUpperCase();

      // DEV-1090. Pre-flight cascade preview. POLICY_TEAM_ROSTERS §3.9 —
      // removing a team member also ends their active/pending role
      // assignments on this Mini-Project. We count those locally from
      // the roles store and surface the cascade in the confirmation so
      // the Lead doesn't end roles by accident.
      const userGuidUpper = member.userGuid.toUpperCase();
      const roles = getRolesForMiniProject(miniProjectGuid);
      const heldRoleCount = roles.reduce((sum, r) => {
        const activeMatch = r.activeAssignees.some(
          (a) => a.userGuid.toUpperCase() === userGuidUpper,
        );
        const pendingMatch = r.pendingInvitations.some(
          (a) => a.userGuid.toUpperCase() === userGuidUpper,
        );
        return sum + (activeMatch ? 1 : 0) + (pendingMatch ? 1 : 0);
      }, 0);

      const basePrompt = isSelf
        ? t("miniProjectTeam.confirmLeave")
        : t("miniProjectTeam.confirmRemove", { name: effectiveTitle(member) });
      const cascadeNotice = heldRoleCount > 0
        ? "\n\n" + t("miniProjectTeam.confirmRemoveCascade", { count: heldRoleCount })
        : "";

      if (!window.confirm(basePrompt + cascadeNotice)) return;
      removeTeamMember(miniProjectGuid, member.teamMemberGuid);
      markDirty();
    },
    [miniProjectGuid, markDirty, currentUserGuid, t],
  );

  /* ── Drag-to-reorder (HTML5 pattern) ── */

  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragIndexRef = useRef<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
    dragIndexRef.current = index;
    setDraggingIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      const srcIndex = dragIndexRef.current;
      setDragOverIndex(null);
      setDraggingIndex(null);
      dragIndexRef.current = null;
      if (srcIndex === null || srcIndex === dropIndex) return;

      const next = [...members];
      const [moved] = next.splice(srcIndex, 1);
      next.splice(dropIndex, 0, moved);
      // Re-stamp sortOrder so the Lead-first sort + sortOrder tiebreak
      // produces the new visual order on re-render.
      const restamped = next.map((m, i) => ({ ...m, sortOrder: i + 1 }));
      setTeamForMiniProject(miniProjectGuid, restamped);
      markDirty();
    },
    [members, miniProjectGuid, markDirty],
  );

  const handleDragEnd = useCallback(() => {
    setDragOverIndex(null);
    setDraggingIndex(null);
    dragIndexRef.current = null;
  }, []);

  /* ── Tray state ──
   *
   * Draft semantics (Skip's 2026-05-20 redirect): the typeahead pick
   * does NOT immediately insert a row into the store. Instead it flips
   * the tray into addingUser mode with the form pre-populated. The
   * Save button on the tray commits; the Cancel button just closes,
   * leaving the store untouched. Same Save/Cancel pattern governs
   * edits to existing rows — the form holds tray-local draft state
   * and only writes back on Save.
   */

  type TrayMode =
    | null
    | "add"                      // typeahead picker visible, no user chosen yet
    | { addingUser: L4eUser }    // user picked, draft form for new row
    | { editingGuid: string };   // editing an existing row

  const [trayMode, setTrayMode] = useState<TrayMode>(null);
  const trayOpen = trayMode !== null;

  const editingMember = useMemo(() => {
    if (trayMode && typeof trayMode === "object" && "editingGuid" in trayMode) {
      return members.find((m) => m.teamMemberGuid === trayMode.editingGuid) || null;
    }
    return null;
  }, [trayMode, members]);

  const closeTray = useCallback(() => setTrayMode(null), []);

  /* ── Add member ── */

  const existingUserGuids = useMemo(
    () => members.map((m) => m.userGuid),
    [members],
  );

  const maxSort = useMemo(
    () => members.reduce((acc, m) => Math.max(acc, m.sortOrder), 0),
    [members],
  );

  // Typeahead pick — does NOT touch the store. Just flips the tray to
  // the new-add draft form. The row is created only when Save fires.
  const handleAddL4E = useCallback(
    (user: L4eUser) => {
      if (atCap) return;
      setTrayMode({ addingUser: user });
    },
    [atCap],
  );

  // Save handler for the new-add path. Inserts the row into the store
  // with the values from the tray's draft state, then closes.
  const handleSaveNewMember = useCallback(
    (user: L4eUser, draft: { displayTitle: string; isHidden: boolean }) => {
      if (atCap) {
        setTrayMode(null);
        return;
      }
      const newMember: LocalMiniProjectTeamMember = {
        teamMemberGuid: generateGuid(),
        miniProjectGuid: miniProjectGuid.toUpperCase(),
        userGuid: user.userGuid,
        role: "member",
        displayTitle: draft.displayTitle.trim() || "Member",
        sortOrder: maxSort + 1,
        isHidden: draft.isHidden,
        invitedByUserGuid: currentUserGuid,
        joinedAt: new Date().toISOString(),
        leftAt: null,
        _localOnly: true,
      };
      upsertTeamMember(miniProjectGuid, newMember);
      markDirty();
      setTrayMode(null);
    },
    [miniProjectGuid, atCap, maxSort, currentUserGuid, markDirty],
  );

  // Save handler for the edit-existing path. Patches displayTitle and
  // isHidden on the row in the store.
  const handleSaveExistingMember = useCallback(
    (member: LocalMiniProjectTeamMember, draft: { displayTitle: string; isHidden: boolean }) => {
      upsertTeamMember(miniProjectGuid, {
        ...member,
        displayTitle: draft.displayTitle.trim() || (member.role === "lead" ? "Lead" : "Member"),
        isHidden: member.role === "lead" ? false : draft.isHidden,
      });
      markDirty();
      setTrayMode(null);
    },
    [miniProjectGuid, markDirty],
  );

  /* ── Transfer Lead state ── */

  const [transferTarget, setTransferTarget] = useState<LocalMiniProjectTeamMember | null>(null);
  const [transferKeepOldOnTeam, setTransferKeepOldOnTeam] = useState(true);

  const currentLead = useMemo(
    () => members.find((m) => m.role === "lead") || null,
    [members],
  );

  const handleOpenTransfer = useCallback((target: LocalMiniProjectTeamMember) => {
    setTransferTarget(target);
    setTransferKeepOldOnTeam(true);
  }, []);

  const handleConfirmTransfer = useCallback(() => {
    if (!transferTarget || !currentLead) return;
    // Mutate the in-memory store atomically: target becomes lead, current
    // Lead becomes member (or is dropped from local list if "stay on team"
    // is unchecked). Server-side bulk save will resolve to the proper
    // transfer-lead semantics at parent Save time.
    const next = members.map((m) => {
      if (m.teamMemberGuid === transferTarget.teamMemberGuid) {
        // Promote the target. If their displayTitle was the default
        // "Member", auto-rename it to "Lead" so the public read is sane.
        return {
          ...m,
          role: "lead",
          displayTitle: m.displayTitle.trim() === "Member" ? "Lead" : m.displayTitle,
        };
      }
      if (m.teamMemberGuid === currentLead.teamMemberGuid) {
        // Demote the old Lead (or drop if not staying).
        if (!transferKeepOldOnTeam) {
          return null;
        }
        return {
          ...m,
          role: "member",
          displayTitle: m.displayTitle.trim() === "Lead" ? "Member" : m.displayTitle,
        };
      }
      return m;
    }).filter((m): m is LocalMiniProjectTeamMember => m !== null);

    setTeamForMiniProject(miniProjectGuid, next);
    markDirty();
    setTransferTarget(null);
  }, [transferTarget, currentLead, transferKeepOldOnTeam, members, miniProjectGuid, markDirty]);

  /* ── Render ── */

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>
            {t("miniProjectTeam.title")}
            <span style={{ ...styles.counter, ...(atCap ? styles.counterAtCap : {}) }}>
              {t("miniProjectTeam.counter", { count: activeCount, cap: TEAM_CAP })}
            </span>
          </div>
          <div style={styles.subtitle}>
            {t("miniProjectTeam.subtitle")}
          </div>
        </div>
        <button
          type="button"
          style={atCap ? { ...styles.addButton, ...styles.addButtonDisabled } : styles.addButton}
          onClick={() => !atCap && setTrayMode("add")}
          disabled={atCap}
          title={atCap ? t("miniProjectTeam.teamCap") : undefined}
        >
          {atCap ? t("miniProjectTeam.teamCap") : t("miniProjectTeam.addMember")}
        </button>
      </div>

      {members.length === 0 ? (
        <div style={styles.emptyState}>
          {t("miniProjectTeam.emptyState")}
        </div>
      ) : (
        <div style={styles.list}>
          {members.map((m, idx) => (
            <MemberListRow
              key={m.teamMemberGuid}
              member={m}
              index={idx}
              isSelf={m.userGuid.toUpperCase() === currentUserGuid.toUpperCase()}
              user={userByGuid.get(m.userGuid.toUpperCase())}
              dragging={draggingIndex === idx}
              dragOver={dragOverIndex === idx && draggingIndex !== idx}
              t={t}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              onEdit={() => setTrayMode({ editingGuid: m.teamMemberGuid })}
              onRemove={() => handleRemove(m)}
              onTransferLead={() => handleOpenTransfer(m)}
            />
          ))}
        </div>
      )}

      {/* ── Roles section ──
          DEV-1088. Sits under the Members list, same tab. Self-contained:
          owns its own data hydration, mutations, and Tray instance.
          Members and Roles coexist (Option C) — accepting a role
          auto-adds the user to mini_project_team_members as 'member'
          server-side, so role-holders appear in the Members list above
          alongside direct adds. */}
      <MiniProjectRolesSection
        miniProjectGuid={miniProjectGuid}
        currentUserGuid={currentUserGuid}
      />

      {/* ── Tray ──
          Three modes: "add" (typeahead), { addingUser } (new-add draft
          form), { editingGuid } (existing-row draft form). Save/Cancel
          semantics: edits live in tray-local draft state and only
          commit on Save. */}
      <Tray
        open={trayOpen}
        onClose={closeTray}
        title={
          trayMode === "add"
            ? t("miniProjectTeam.tray.addTitle")
            : trayMode && typeof trayMode === "object" && "addingUser" in trayMode
              ? t("miniProjectTeam.tray.addTitle")
              : editingMember
                ? t("miniProjectTeam.tray.editTitle", { name: effectiveTitle(editingMember) })
                : ""
        }
      >
        {trayMode === "add" && (
          <AddMemberTrayBody
            onAddL4E={handleAddL4E}
            onCancel={closeTray}
            allUsers={allUsers}
            excludeGuids={existingUserGuids}
            currentUserGuid={currentUserGuid}
            t={t}
          />
        )}
        {trayMode && typeof trayMode === "object" && "addingUser" in trayMode && (
          <MemberFormTrayBody
            mode="add"
            user={trayMode.addingUser}
            initialDisplayTitle="Member"
            initialIsHidden={false}
            isLead={false}
            onSave={(draft) => handleSaveNewMember(trayMode.addingUser, draft)}
            onCancel={closeTray}
            t={t}
          />
        )}
        {editingMember && (
          <MemberFormTrayBody
            mode="edit"
            member={editingMember}
            user={userByGuid.get(editingMember.userGuid.toUpperCase())}
            initialDisplayTitle={editingMember.displayTitle}
            initialIsHidden={editingMember.isHidden}
            isLead={editingMember.role === "lead"}
            onSave={(draft) => handleSaveExistingMember(editingMember, draft)}
            onCancel={closeTray}
            onRemove={() => {
              handleRemove(editingMember);
              closeTray();
            }}
            t={t}
          />
        )}
      </Tray>

      {/* ── Transfer Lead confirmation ── */}
      {transferTarget && currentLead && (
        <div style={styles.confirmBackdrop} onClick={(e) => {
          if (e.target === e.currentTarget) setTransferTarget(null);
        }}>
          <div style={styles.confirmModal} role="dialog" aria-modal="true">
            <h3 style={styles.confirmTitle}>
              {t("miniProjectTeam.transferLeadTitle")}
            </h3>
            <p style={styles.confirmBody}>
              {t("miniProjectTeam.transferLeadBody", { name: effectiveTitle(transferTarget) })}
            </p>
            <label style={styles.toggleRow}>
              <input
                type="checkbox"
                checked={transferKeepOldOnTeam}
                onChange={(e) => setTransferKeepOldOnTeam(e.target.checked)}
                style={{ marginBlockStart: 2 }}
              />
              <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                  {t("miniProjectTeam.transferLeadStayOnTeam")}
                </span>
                <span style={styles.helpText}>
                  {t("miniProjectTeam.transferLeadStayHelp")}
                </span>
              </span>
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                style={styles.smallButton}
                onClick={() => setTransferTarget(null)}
              >
                {t("miniProjectTeam.cancel")}
              </button>
              <button
                type="button"
                style={styles.primaryButton}
                onClick={handleConfirmTransfer}
              >
                {t("miniProjectTeam.transferLeadConfirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiniProjectTeamTab;

/* ──────────────────────────────────────────────────────────────────
 *  Sub-components
 * ────────────────────────────────────────────────────────────────── */

type TFn = (key: string, options?: Record<string, unknown>) => string;

interface MemberListRowProps {
  member: LocalMiniProjectTeamMember;
  index: number;
  isSelf: boolean;
  /** Resolved user record (undefined if securityUsers hasn't loaded or
   *  the user_guid is no longer in the platform). Used to render a
   *  proper display name and avatar instead of a truncated GUID. */
  user: L4eUser | undefined;
  dragging: boolean;
  dragOver: boolean;
  t: TFn;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
  onEdit: () => void;
  onRemove: () => void;
  onTransferLead: () => void;
}

function MemberListRow({
  member,
  index,
  isSelf,
  user,
  dragging,
  dragOver,
  t,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onEdit,
  onRemove,
  onTransferLead,
}: MemberListRowProps) {
  const isLead = member.role === "lead";
  const rowStyle: React.CSSProperties = {
    ...styles.listRow,
    ...(isLead ? styles.listRowLead : {}),
    ...(dragging ? styles.listRowDragging : {}),
    ...(dragOver ? styles.listRowDragOver : {}),
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={(e) => onDrop(e, index)}
      onDragEnd={onDragEnd}
      style={rowStyle}
      onClick={onEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit();
        }
      }}
    >
      <span style={styles.dragHandle}>⋮⋮</span>
      {smallAvatar(user)}
      <div style={styles.rowText}>
        <div style={styles.rowTitle}>
          {effectiveTitle(member)}
          {isLead && <span style={styles.badge}>{t("miniProjectTeam.leadBadge")}</span>}
          {member.isHidden && <span style={styles.hiddenBadge}>{t("miniProjectTeam.hiddenBadge")}</span>}
        </div>
        <div style={styles.rowName}>
          {resolveUserDisplayName(user, member.userGuid)}{isSelf ? ` (${t("miniProjectTeam.you")})` : ""}
        </div>
      </div>
      <div style={styles.rowActions} onClick={(e) => e.stopPropagation()}>
        {!isLead && (
          <button
            type="button"
            style={styles.smallButton}
            onClick={onTransferLead}
            title={t("miniProjectTeam.transferLeadTooltip")}
          >
            {t("miniProjectTeam.transferLeadShort")}
          </button>
        )}
        <button type="button" style={styles.smallButton} onClick={onEdit}>
          {t("miniProjectTeam.edit")}
        </button>
        {!isLead && (
          <button
            type="button"
            style={{ ...styles.smallButton, ...styles.dangerButton }}
            onClick={onRemove}
          >
            {isSelf ? t("miniProjectTeam.leave") : t("miniProjectTeam.remove")}
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Tray body: Add member (any L4E user) ─────────────────────────────
 *
 * POLICY_TEAM_ROSTERS.md §3.4 — picker scope is "any active L4E user,"
 * not just accepted contacts. The added user gets a bell notification
 * but does not need to accept before appearing on the team. Same
 * inline-typeahead pattern as RoleAssignmentModal / OA panel /
 * CommunityPermissionsV2Panel — filter the pre-loaded securityUsers
 * snapshot client-side.
 */

interface AddMemberTrayBodyProps {
  onAddL4E: (user: L4eUser) => void;
  onCancel: () => void;
  allUsers: L4eUser[];
  excludeGuids: string[];
  currentUserGuid: string;
  t: TFn;
}

function AddMemberTrayBody({
  onAddL4E,
  onCancel,
  allUsers,
  excludeGuids,
  currentUserGuid,
  t,
}: AddMemberTrayBodyProps) {
  const [search, setSearch] = useState("");

  // Build the filtered, alphabetized pool. Mirrors RoleAssignmentModal:
  // exclude already-on-team users; case-insensitive substring match
  // across displayName / name / email / screenName; alphabetical order
  // with numeric-aware locale compare; cap to a reasonable display
  // length so the dropdown doesn't blow up on first focus.
  const excludeSet = useMemo(() => {
    const s = new Set(excludeGuids.map((g) => g.toUpperCase()));
    s.add(currentUserGuid.toUpperCase()); // hide self from the picker too
    return s;
  }, [excludeGuids, currentUserGuid]);

  const userOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Don't dump the whole user list on first focus. The dropdown only
    // populates once the admin starts typing — surfaces the "Start
    // typing to search" empty-state hint instead.
    if (!q) return [];
    const pool = allUsers.filter((u) => !excludeSet.has(u.userGuid.toUpperCase()));
    const filtered = pool.filter(
      (u) =>
        (u.displayName ?? "").toLowerCase().includes(q) ||
        (u.name ?? "").toLowerCase().includes(q) ||
        (u.email ?? "").toLowerCase().includes(q) ||
        (u.screenName ?? "").toLowerCase().includes(q),
    );
    return filtered
      .slice()
      .sort((a, b) =>
        (a.displayName || a.name || a.email || "").localeCompare(
          b.displayName || b.name || b.email || "",
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
      )
      .slice(0, 50);
  }, [allUsers, excludeSet, search]);

  return (
    <>
      <Tray.Body>
        <div style={styles.trayBody}>
          <div style={styles.helpText}>
            {t("miniProjectTeam.tray.addHelp")}
          </div>
          <div>
            <div style={styles.fieldLabel}>{t("miniProjectTeam.tray.searchLabel")}</div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("miniProjectTeam.tray.searchPlaceholder")}
              style={styles.inlineInput}
              autoFocus
            />
          </div>
          <div style={styles.userResults}>
            {userOptions.length === 0 ? (
              <div style={styles.userResultEmpty}>
                {search.trim()
                  ? t("miniProjectTeam.tray.noResults")
                  : t("miniProjectTeam.tray.startTyping")}
              </div>
            ) : (
              userOptions.map((u) => (
                <button
                  type="button"
                  key={u.userGuid}
                  style={styles.userResultItem}
                  onClick={() => onAddL4E(u)}
                >
                  <div style={styles.userResultAvatar}>
                    {u.profileImageUrl ? (
                      <img
                        src={mediaSrc(u.profileImageUrl) ?? undefined}
                        alt=""
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      (u.displayName || u.name || u.email || "?").trim().charAt(0).toUpperCase()
                    )}
                  </div>
                  <div style={styles.userResultText}>
                    <div style={styles.userResultName}>
                      {u.displayName || u.name || u.screenName || `${u.userGuid.slice(0, 8)}…`}
                    </div>
                    {u.email && <div style={styles.userResultEmail}>{u.email}</div>}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </Tray.Body>
      <Tray.Footer>
        <div style={styles.trayFooter}>
          <button type="button" style={styles.smallButton} onClick={onCancel}>
            {t("miniProjectTeam.cancel")}
          </button>
        </div>
      </Tray.Footer>
    </>
  );
}

/* ── Tray body: Member form (handles both new-add and edit-existing) ──
 *
 * Holds draft state for displayTitle and isHidden. Save commits to the
 * parent's handler (which writes to the in-memory store); Cancel just
 * closes. FlipTeamMemberCard preview at the top renders live as the
 * admin types so they see how the member will appear publicly — same
 * pattern Skip uses on CommunityEnablersTab.
 */

interface MemberFormTrayBodyProps {
  mode: "add" | "edit";
  /** Only set in edit mode — used by the Remove button. */
  member?: LocalMiniProjectTeamMember;
  /** Resolved L4E user record for the FlipTeamMemberCard preview. */
  user: L4eUser | undefined;
  initialDisplayTitle: string;
  initialIsHidden: boolean;
  isLead: boolean;
  onSave: (draft: { displayTitle: string; isHidden: boolean }) => void;
  onCancel: () => void;
  /** Only present in edit mode for non-Lead rows. */
  onRemove?: () => void;
  t: TFn;
}

function MemberFormTrayBody({
  mode,
  user,
  initialDisplayTitle,
  initialIsHidden,
  isLead,
  onSave,
  onCancel,
  onRemove,
  t,
}: MemberFormTrayBodyProps) {
  // Tray-local draft state. Resets when the tray re-mounts (i.e., when
  // the parent flips trayMode), which is the desired snapshot behavior.
  const [displayTitle, setDisplayTitle] = useState(initialDisplayTitle);
  const [isHidden, setIsHidden] = useState(initialIsHidden);

  // Preview-card values. Live-update from draft state so the admin
  // sees their edits reflected in the public-facing card.
  const previewTitle = (displayTitle || "").trim()
    || (isLead ? "Lead" : "Member");
  const previewName = user?.displayName || user?.name || user?.screenName || "";

  return (
    <>
      <Tray.Body>
        <div style={styles.trayBody}>
          {/* Live preview — same FlipTeamMemberCard the public sees.
              Mirrors CommunityEnablersTab line 873-892 so admins on
              both surfaces get the same "WYSIWYG" feel. */}
          <div style={styles.cardPreviewWrap}>
            <FlipTeamMemberCard
              displayName={previewName}
              photoUrl={user?.profileImageUrl || undefined}
              roleTitle={previewTitle}
              linkedUserGuid={user?.userGuid}
              userGuid={user?.userGuid}
              contactEmail={user?.email || undefined}
              backFaceBadge={isLead ? t("miniProjectTeam.leadBadge") : t("miniProjectTeam.memberBadge")}
              avatarVariant="l4e"
              splitName
              size="lg"
            />
          </div>

          <div style={styles.fieldGroup}>
            <div>
              <div style={styles.fieldLabel}>{t("miniProjectTeam.displayTitleLabel")}</div>
              <input
                type="text"
                value={displayTitle}
                onChange={(e) => setDisplayTitle(e.target.value)}
                placeholder={t("miniProjectTeam.displayTitlePlaceholder")}
                style={styles.inlineInput}
                autoFocus
              />
              <div style={styles.helpText}>
                {t("miniProjectTeam.displayTitleHelp")}
              </div>
            </div>

            {/* Hidden toggle. POLICY-locked: Lead cannot be hidden. */}
            <label
              style={{
                ...styles.toggleRow,
                ...(isLead ? styles.toggleRowDisabled : {}),
              }}
            >
              <input
                type="checkbox"
                checked={isHidden}
                onChange={(e) => setIsHidden(e.target.checked)}
                disabled={isLead}
                style={{ marginBlockStart: 2 }}
              />
              <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                  {t("miniProjectTeam.hiddenLabel")}
                </span>
                <span style={styles.helpText}>
                  {isLead
                    ? t("miniProjectTeam.leadHiddenHelp")
                    : t("miniProjectTeam.hiddenHelp")}
                </span>
              </span>
            </label>
          </div>
        </div>
      </Tray.Body>
      <Tray.Footer>
        <div style={styles.trayFooter}>
          {/* Remove only available in edit mode for non-Lead rows. */}
          {mode === "edit" && !isLead && onRemove && (
            <button
              type="button"
              style={{ ...styles.smallButton, ...styles.dangerButton }}
              onClick={onRemove}
            >
              {t("miniProjectTeam.remove")}
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button type="button" style={styles.smallButton} onClick={onCancel}>
            {t("miniProjectTeam.cancel")}
          </button>
          <button
            type="button"
            style={styles.primaryButton}
            onClick={() => onSave({ displayTitle, isHidden })}
          >
            {t("miniProjectTeam.save")}
          </button>
        </div>
      </Tray.Footer>
    </>
  );
}
